import {Injectable} from '@angular/core'
import {concatMap, delay, from, Observable, of, reduce, shareReplay, switchMap, takeUntil, timer} from 'rxjs'
import {HttpParams} from "@angular/common/http";
import {PaginatedResponse, SingleResponse} from "@generated-backend-types";
import {ActivatedRouteSnapshot} from "@angular/router";
import {map} from "rxjs/operators";
import {ApiService} from './api.service';
import {ListItem} from '../../entities/list-item.entity';


@Injectable({
  providedIn: 'root'
})
export abstract class BaseEndpointService<ModelResource extends BaseResource, ModelData = ModelResource, IndexResource = ModelResource> extends ApiService {

  public readonly multiSelectSortBy?: string;
  protected endpoint = ''
  private cacheExpiryTimeForGetRequests = 10000; // 10 seconds
  private cachedGetResults: {
    [id: string]: { result: Observable<SingleResponse<ModelResource>>, cachedAt: number }
  } = {};
  private cachedFindResults: {
    [id: string]: { result: Observable<PaginatedResponse<IndexResource>>, cachedAt: number }
  } = {};
  private cachedGetAllResults: {
    [id: string]: { result: Observable<PaginatedResponse<IndexResource>>, cachedAt: number }
  } = {};

  public resolve(route: ActivatedRouteSnapshot): Observable<ModelResource> {
    return this.get(route.params['id']).pipe(map(r => r.data))
  }

  public find(page = 1, query: string, withoutCache = false): Observable<PaginatedResponse<IndexResource>> {

    const cacheKey = JSON.stringify({endpoint: this.endpoint, page, query})
    const cachedResult = this.cachedFindResults[cacheKey];

    if (!withoutCache && cachedResult && cachedResult.cachedAt > Date.now() - this.cacheExpiryTimeForGetRequests) {
      return cachedResult.result;
    }
    delete this.cachedGetAllResults[cacheKey];

    const result = this.http.get<PaginatedResponse<IndexResource>>(this.endpoint, {
      params: {
        page,
        q: query
      }
    }).pipe(
      takeUntil(timer(this.cacheExpiryTimeForGetRequests)),
      shareReplay(1)
    );
    this.cachedFindResults[cacheKey] = {result, cachedAt: Date.now()};
    return result;

  }

  protected multiSelectTextFields: readonly (keyof ModelResource)[] = ['title' as keyof ModelResource];

  public modelToMultiSelectItem(m: ModelResource): ListItem & { model: ModelResource } {
    const text = this.multiSelectTextFields
      .map(field => m[field])
      .filter(v => v !== null && v !== undefined && v !== '')
      .join(' ');
    return { id: m.id as string, text, isDisabled: false, model: m };
  }

  public getPage(page = 1, params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }, withoutCache = false): Observable<PaginatedResponse<IndexResource>> {

    const cacheKey = JSON.stringify({endpoint: this.endpoint, page, params})
    const cachedResult = this.cachedGetAllResults[cacheKey];
    if (!withoutCache && cachedResult && cachedResult.cachedAt > Date.now() - this.cacheExpiryTimeForGetRequests) {
      return cachedResult.result;
    }
    delete this.cachedGetAllResults[cacheKey];
    const result = this.http.get<PaginatedResponse<IndexResource>>(this.endpoint, {
      params: {
        ...params,
        page
      },
    }).pipe(
      takeUntil(timer(this.cacheExpiryTimeForGetRequests)),
      shareReplay(1)
    );
    this.cachedGetAllResults[cacheKey] = {result, cachedAt: Date.now()};
    return result;
  }

  public loadAllPages(params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }, withoutCache = false): Observable<IndexResource[]> {

    return this.getPage(1, params, withoutCache).pipe(
      switchMap(firstPage => {
        const allPages = [firstPage];
        const pagesToFetch = firstPage.meta.last_page - 1;
        const pageNumbers = Array.from({length: pagesToFetch}, (_, i) => i + 2);


        // if there is more to load => load it
        return from(pageNumbers).pipe(
          // but with a delay of 1000ms per page
          concatMap(pageNumber => of(null).pipe(delay(1000), switchMap(() => this.getPage(pageNumber, params, withoutCache)))),
          reduce((acc, current: PaginatedResponse<IndexResource>) => [...acc, ...current.data], allPages[0].data)
        );
      })
    );
  }

  public create(data: ModelData): Observable<SingleResponse<ModelResource>> {
    return this.http.post<SingleResponse<ModelResource>>(this.endpoint, data);
  }

  public get(id: string, withoutCache = false): Observable<SingleResponse<ModelResource>> {
    const cachedResult = this.cachedGetResults[id];

    if (!withoutCache && cachedResult && cachedResult.cachedAt > Date.now() - this.cacheExpiryTimeForGetRequests) {
      return cachedResult.result;
    }
    delete this.cachedGetResults[id];
    const result = this.http.get<SingleResponse<ModelResource>>(this.endpoint + '/' + id).pipe(
      takeUntil(timer(this.cacheExpiryTimeForGetRequests)),
      shareReplay(1)
    );
    this.cachedGetResults[id] = {result, cachedAt: Date.now()};
    return result;
  }

  public update(id: string, data: ModelData): Observable<SingleResponse<ModelResource>> {
    return this.http.put<SingleResponse<ModelResource>>(this.endpoint + '/' + id, data)
  }

  public delete(id: string): Observable<void> {
    return this.http.delete<void>(this.endpoint + '/' + id)
  }
}
